import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DesignCard from "@/components/DesignCard";
import { Search, Grid3X3, LayoutGrid, Minus, Clock, TrendingUp, Hexagon, Leaf, Star, Sparkles } from "lucide-react";

const iconMap = {
  grid: Grid3X3,
  minus: Minus,
  clock: Clock,
  "trending-up": TrendingUp,
  hexagon: Hexagon,
  leaf: Leaf,
  star: Star,
  sparkles: Sparkles,
};

export default function Marketplace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [designs, setDesigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const activeCategory = searchParams.get("category") || "all";

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (e) {
        console.error("Failed to fetch categories");
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchDesigns = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeCategory !== "all") params.append("category", activeCategory);
        if (searchQuery) params.append("search", searchQuery);
        
        const response = await axios.get(`${API}/designs?${params.toString()}`);
        setDesigns(response.data);
      } catch (e) {
        console.error("Failed to fetch designs");
      } finally {
        setLoading(false);
      }
    };
    fetchDesigns();
  }, [activeCategory, searchQuery]);

  const handleCategoryChange = (categoryId) => {
    if (categoryId === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", categoryId);
    }
    setSearchParams(searchParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already reactive via the searchQuery state
  };

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="marketplace-page">
      <Navbar />

      <main className="pt-32 pb-16 container-padding">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <p className="overline mb-4">EXPLORE DESIGNS</p>
            <h1 
              className="text-4xl sm:text-5xl font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Marketplace
            </h1>
            <p className="mt-4 text-lg text-zinc-600 max-w-2xl">
              Discover premium t-shirt designs for your print-on-demand business. 
              Filter by category or search for specific styles.
            </p>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-col lg:flex-row gap-6 mb-12">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search designs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field pl-12"
                  data-testid="marketplace-search"
                />
              </div>
            </form>

            {/* Category Pills */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const IconComponent = iconMap[category.icon] || Grid3X3;
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeCategory === category.id
                        ? "bg-zinc-900 text-white"
                        : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-900"
                    }`}
                    data-testid={`category-${category.id}`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-sm text-zinc-500">
              {loading ? "Loading..." : `${designs.length} design${designs.length !== 1 ? "s" : ""} found`}
            </p>
          </div>

          {/* Designs Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="skeleton-pulse rounded-lg">
                  <div className="aspect-square"></div>
                  <div className="p-4 space-y-2">
                    <div className="h-4 rounded"></div>
                    <div className="h-3 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : designs.length === 0 ? (
            <div className="empty-state">
              <LayoutGrid className="w-16 h-16 text-zinc-300 mb-4" />
              <h3 className="text-xl font-medium mb-2">No designs found</h3>
              <p className="text-zinc-500 mb-6">
                Try adjusting your search or filter to find what you're looking for.
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  handleCategoryChange("all");
                }}
                className="btn-secondary"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {designs.map((design, idx) => (
                <div
                  key={design.id}
                  className="animate-fade-in-up opacity-0"
                  style={{ animationDelay: `${0.05 * idx}s`, animationFillMode: 'forwards' }}
                >
                  <DesignCard design={design} />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
